import {Helmet} from '@dr.pogodin/react-helmet';
import {BreadcrumbType, Radio, Switch} from 'platform/components';
import {Link, Show, Text, VStack} from 'platform/foundation';

import {
  PhotoCheckerMode,
  useGetPhotoCheckerConfigQuery,
  usePatchPhotoCheckerConfigMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {formatDocumentTitle, handleApiError} from '@dms/shared';

import {useNavigate} from 'shared/web';

const PHOTO_CHECKER_DOCS_URL = 'https://help.omnetic.com/docs/photochecker';

export function PhotoCheckerSettings() {
  const navigate = useNavigate();
  const {data: photoCheckerConfig, isLoading, isError} = useGetPhotoCheckerConfigQuery();
  const [patchPhotoCheckerConfig, {isLoading: isPatching}] = usePatchPhotoCheckerConfigMutation();

  const breadcrumbs: BreadcrumbType[] = [
    {
      label: i18n.t('page.salesSettings.labels.advertising'),
      href: settingsRoutes.advertising,
    },
  ];

  const handleToggleEnabled = (enabled: boolean) =>
    patchPhotoCheckerConfig({enabled}).unwrap().catch(handleApiError);

  const handleModeChange = (mode: PhotoCheckerMode) =>
    patchPhotoCheckerConfig({mode}).unwrap().catch(handleApiError);

  return (
    <>
      <Helmet
        title={formatDocumentTitle(
          i18n.t('page.integrations.labels.photoChecker'),
          i18n.t('page.salesSettings.labels.advertising'),
          i18n.t('page.settings.labels.title')
        )}
      />
      <SettingsTemplate
        data-testid={testIds.settings.photoChecker('settingsTemplate')}
        header={{
          title: i18n.t('page.integrations.labels.photoChecker'),
          breadcrumbs,
        }}
        isLoading={isLoading}
        isError={isError}
      >
        <SettingsSection data-testid={testIds.settings.photoChecker('settingsSection')}>
          <VStack spacing={4}>
            <Text size="small" color="secondary">
              {i18n.t('page.integrations.labels.photoCheckerDescription')}
            </Text>
            <Link href={PHOTO_CHECKER_DOCS_URL} target="_blank" size="small">
              {i18n.t('general.actions.viewDocumentation')}
            </Link>

            <Switch
              value={photoCheckerConfig?.enabled ?? false}
              onChange={handleToggleEnabled}
              isDisabled={isPatching}
              label={i18n.t('page.integrations.labels.photoCheckerEnable')}
              data-testid={testIds.settings.photoChecker('enablePhotoChecker')}
            />

            <Show when={photoCheckerConfig?.enabled}>
              <VStack spacing={4}>
                <Radio
                  label={i18n.t('page.integrations.labels.photoCheckerMode')}
                  value={photoCheckerConfig?.mode ?? 'manual'}
                  onChange={(mode) => {
                    if (mode === 'manual' || mode === 'auto') {
                      handleModeChange(mode);
                    }
                  }}
                  isDisabled={isPatching}
                  options={[
                    {
                      label: i18n.t('page.integrations.labels.photoCheckerModeManual'),
                      value: 'manual',
                    },
                    {
                      label: i18n.t('page.integrations.labels.photoCheckerModeAuto'),
                      value: 'auto',
                    },
                  ]}
                  data-testid={testIds.settings.photoChecker('mode')}
                />

                <Link
                  size="small"
                  title={i18n.t('page.integrations.labels.photoCheckerNotificationSettings')}
                  data-testid={testIds.settings.photoChecker('notificationSettings')}
                  onClick={() => navigate(settingsRoutes.notificationsOverview)}
                />
              </VStack>
            </Show>
          </VStack>
        </SettingsSection>
      </SettingsTemplate>
    </>
  );
}
